import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";
import { Reveal, Overline } from "./Reveal";
import { CHAPTERS, SERVICES, GALLERY, GALLERY_CATS, STOCK, REAL } from "@/data";
import { useBooking } from "@/context/booking";

export const About = () => (
  <section id="about" className="relative py-24 md:py-32 bg-[#0A0A0A] overflow-hidden">
    <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-16 items-start">
      <div>
        <Reveal><Overline>About Epic Memories LLC</Overline></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display text-4xl md:text-6xl mt-5 leading-[1.05]">
            Affordable luxury, <span className="italic gold-text">rooted in culture</span> & community.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-zinc-400 mt-6 text-lg leading-relaxed max-w-lg">
            A Black woman-owned event company in Little Rock, Arkansas, dedicated to creating unforgettable
            experiences that bring communities together through creativity, culture and celebration.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative mt-10 clip-frame overflow-hidden rounded-lg">
            <img src={REAL.event2} alt="Epic Memories community event" className="w-full h-72 object-cover" />
          </div>
        </Reveal>
      </div>

      <div className="space-y-2">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.08}>
            <div className="group border-t border-white/10 py-7 flex gap-6 hover:pl-2 transition-all duration-300">
              <span className="font-display italic text-4xl gold-text w-16 shrink-0">{c.n}</span>
              <div>
                <h3 className="font-heading font-bold text-xl group-hover:text-[#D4AF37] transition-colors">{c.t}</h3>
                <p className="text-zinc-400 mt-2 leading-relaxed">{c.d}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Services = () => {
  const { openBooking } = useBooking();
  return (
    <section id="services" className="relative py-24 md:py-32 bg-[#0d0d0d]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <Reveal><Overline>What We Create</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-heading font-black text-3xl md:text-5xl mt-4 max-w-2xl">
                Every occasion, elevated to <span className="gold-text">epic</span>.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <button data-testid="services-book-btn" onClick={() => openBooking()}
              className="self-start border border-white/25 text-white rounded-full px-6 py-3 font-heading font-bold hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
              Book a Service
            </button>
          </Reveal>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 auto-rows-[180px] gap-4 mt-14">
          {SERVICES.map((s, i) => (
            <Reveal key={s.t} delay={(i % 4) * 0.05} className={s.tall ? "row-span-2" : ""}>
              <button data-testid={`service-${i}`} onClick={() => openBooking(s.t)}
                className={`group relative w-full h-full overflow-hidden rounded-lg border border-white/10 text-left p-6 flex flex-col justify-between transition-colors hover:border-[#D4AF37]/50 ${s.img ? "" : "bg-[#141414]"}`}>
                {s.img && <>
                  <img src={s.img} alt={s.t} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                </>}
                <div className="relative flex justify-between items-start">
                  <span className="font-display italic text-sm text-zinc-500">{String(i + 1).padStart(2, "0")}</span>
                  <ArrowUpRight size={20} className="text-zinc-500 group-hover:text-[#D4AF37] group-hover:rotate-45 transition-all duration-300" />
                </div>
                <div className="relative">
                  <h3 className="font-heading font-bold text-lg leading-tight group-hover:text-[#D4AF37] transition-colors">{s.t}</h3>
                  <p className="text-sm text-zinc-400 mt-1.5 leading-snug">{s.d}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Gallery = () => {
  const [cat, setCat] = useState("All");
  const items = cat === "All" ? GALLERY : GALLERY.filter((g) => g.cat === cat);
  return (
    <section id="gallery" className="relative py-24 md:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal><Overline>The Gallery</Overline></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-heading font-black text-3xl md:text-5xl mt-4">Moments we&apos;ve made <span className="gold-text">real</span>.</h2>
        </Reveal>

        <div className="flex flex-wrap gap-2 mt-8">
          {GALLERY_CATS.map((c) => (
            <button key={c} data-testid={`gallery-filter-${c.replace(/\s/g, "-").toLowerCase()}`} onClick={() => setCat(c)}
              className={`text-sm font-medium rounded-full px-4 py-2 transition-colors ${cat === c ? "bg-[#D4AF37] text-black" : "border border-white/15 text-zinc-300 hover:border-[#D4AF37]"}`}>
              {c}
            </button>
          ))}
        </div>

        <motion.div layout className="columns-2 md:columns-3 gap-4 mt-10 [&>*]:mb-4">
          {items.map((g, i) => (
            <motion.div layout key={g.src + i} initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: (i % 3) * 0.05 }}
              className="relative overflow-hidden rounded-lg group break-inside-avoid">
              <img src={g.src} alt={g.cat} className="w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <span className="text-[#D4AF37] text-xs font-semibold uppercase tracking-widest">{g.cat}</span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
